import React, { useState, useEffect } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { 
  CreditCard, ArrowLeft, Edit, Calendar, DollarSign, Building2, User, 
  Mail, ShieldCheck, Clock, CheckCircle, XCircle, AlertCircle, 
  MoreHorizontal, RefreshCw, Zap, Shield, FileText, Banknote, Save,
  TrendingUp, Activity, Archive, PauseCircle, PlayCircle, Link, ExternalLink,
  BarChart3, Repeat, List, Download, Eye, Trash2, ChevronLeft, 
  FileCheck, Users, Info, ArrowUpRight, History, Hourglass
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate, useParams } from 'react-router-dom';
import { Can } from '../../components/Can';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DirectDebit, DirectDebitTransaction } from '../../types/direct-debit';
import { financeApi } from '../../utils/finance-api';
import { toast } from 'sonner';
import { DeleteAttachmentDialog } from '../../components/finance/DeleteAttachmentDialog';

const DirectDebitDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debit, setDebit] = useState<DirectDebit | null>(null);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  
  // Transaction Edit States
  const [showTxDialog, setShowTxDialog] = useState(false);
  const [selectedTx, setSelectedTx] = useState<DirectDebitTransaction | null>(null);
  const [txForm, setTxTxForm] = useState({
    amount: 0,
    dueDate: '',
    status: 'pending' as DirectDebitTransaction['status'],
    notes: '',
    adjustmentReason: '',
    actualAmount: 0 as number | null,
    processedDate: '' as string | null
  });

  // Attachment Delete States
  const [showDeleteAttDialog, setShowDeleteAttDialog] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{id: string, name: string} | null>(null);

  useEffect(() => {
    if (id) loadDebit();
  }, [id]);

  const loadDebit = async () => {
    setIsLoading(true);
    try {
      const [debitData, logsData] = await Promise.all([
        financeApi.getDirectDebit(id!),
        financeApi.getActivityLogs(id!)
      ]);
      setDebit(debitData);
      setActivityLogs(logsData);
    } catch (error) {
      toast.error("Failed to load direct debit details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTx = (tx: DirectDebitTransaction) => {
    setSelectedTx(tx);
    setTxTxForm({
      amount: tx.amount,
      dueDate: tx.dueDate ? tx.dueDate.split('T')[0] : '',
      status: tx.status,
      notes: tx.notes || '',
      adjustmentReason: tx.adjustmentReason || '',
      actualAmount: tx.actualAmount ?? null,
      processedDate: tx.processedDate ? tx.processedDate.split('T')[0] : (tx.status === 'processed' ? new Date().toISOString().split('T')[0] : '')
      });
    setShowTxDialog(true);
  };

  const handleSaveTx = async () => {
    if (!selectedTx) return;
    try {
      // Ensure actualAmount and processedDate are only sent if status is 'processed'
      // Otherwise, these should be null to avoid overriding the scheduled 'amount' in trends
      const updateData = {
        ...txForm,
        actualAmount: txForm.status === 'processed' ? txForm.actualAmount : null,
        processedDate: txForm.status === 'processed' ? txForm.processedDate : null
      };

      const sanitizedData = Object.entries(updateData).reduce((acc, [key, value]) => {
        acc[key] = value === '' ? null : value;
        return acc;
      }, {} as any);

      await financeApi.updateTransaction(selectedTx.id, sanitizedData);
      toast.success("Transaction updated successfully");
      setShowTxDialog(false);
      loadDebit();
    } catch (error) {
      toast.error("Failed to update transaction");
    }
  };

  const handleDeleteAttachment = (attId: string, fileName: string) => {
    setAttachmentToDelete({ id: attId, name: fileName });
    setShowDeleteAttDialog(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;
    try {
      await financeApi.deleteAttachment(attachmentToDelete.id);
      toast.success("Attachment deleted successfully");
      setShowDeleteAttDialog(false);
      setAttachmentToDelete(null);
      loadDebit();
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const handleToggleStatus = async () => {
    if (!debit) return;
    try {
      const newStatus = debit.status === 'active' ? 'suspended' : 'active';
      await financeApi.updateDirectDebit(debit.id, { status: newStatus });
      toast.success(`Direct debit ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      loadDebit();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatCurrency = (amount: number, currency: string = "AUD") => {
    const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
    }).format(safeAmount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "approved":
      case "processed":
        return "bg-green-100 text-green-700 border-green-200";
      case "pending":
      case "scheduled":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "failed":
      case "rejected":
        return "bg-red-100 text-red-700 border-red-200";
      case "suspended":
        return "bg-gray-100 text-gray-700 border-gray-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getApprovalStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span>Loading Details...</span>
      </div>
    );
  }

  if (!debit) return <div className="p-8 text-center text-muted-foreground">Direct debit record not found.</div>;

  // Calculate Transaction Stats for the Summary Banner (Schedule Tab)
  // Pending transactions are considered non-existent if the debit is not active
  const scheduledTransactions = (debit.transactions || []).filter(tx => 
    (tx.status === 'pending' || tx.status === 'scheduled') && debit.status === 'active'
  );
  const processedTransactions = (debit.transactions || []).filter(tx => tx.status === 'processed');
  const failedTransactions = (debit.transactions || []).filter(tx => tx.status === 'failed');
  const adjustmentTransactions = (debit.transactions || []).filter(tx => tx.type === 'adjustment');

  const totalScheduledAmount = scheduledTransactions.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
  const totalProcessedAmount = processedTransactions.reduce((sum, tx) => sum + (Number(tx.actualAmount || tx.amount) || 0), 0);

  const avgProcessedAmount = processedTransactions.length > 0 
    ? totalProcessedAmount / processedTransactions.length 
    : 0;

  return (
    <div className="container mx-auto px-4 py-8 space-y-8 max-w-7xl">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/finance/direct-debit')} className="h-9 w-9 mt-1">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{debit.description}</h1>
            <p className="text-sm font-medium text-slate-500 mt-1">{debit.department} • {debit.referenceNumber || 'No Reference'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="bg-white border-slate-200 text-slate-700 h-9 px-4 font-bold" onClick={handleToggleStatus}>
            {debit.status === 'active' ? (
              <>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 mr-2" />
                Resume
              </>
            )}
          </Button>
          <Can I={ACTIONS.UPDATE} a={RESOURCES.DIRECT_DEBIT}>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 shadow-sm font-bold" onClick={() => navigate(`/finance/direct-debit/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Details
            </Button>
          </Can>
        </div>
      </div>

      {/* Summary Banner Card */}
      <Card className="border-slate-200 shadow-sm overflow-hidden" style={{ background: 'linear-gradient(to right, #EFF6FF, #FAF5FF)' }}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{debit.description}</h2>
                <p className="text-slate-500 font-medium">{debit.company && typeof debit.company === 'object' ? (debit.company as any).name : debit.company || 'Unknown Company'}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={`${getStatusColor(debit.status)} capitalize border-transparent font-bold h-6 px-3`}>
                  {debit.status === 'suspended' ? 'inactive' : debit.status}
                </Badge>
                <Badge variant="outline" className={`${getApprovalStatusColor(debit.approvalStatus)} capitalize border-transparent font-bold h-6 px-3`}>
                  {debit.approvalStatus}
                </Badge>
                <Badge variant="outline" className="bg-slate-100 text-slate-600 border-transparent font-bold h-6 px-3 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <span className="capitalize">{debit.frequency}</span>
                </Badge>
              </div>
            </div>
            <div className="flex flex-col md:items-end justify-center">
              <div className="text-2xl font-semibold text-slate-900">
                {formatCurrency(debit.monthlyDebitAmount, debit.currency)}
              </div>
              <div className="text-sm font-bold text-slate-500 mt-1 ">
                Total: {formatCurrency(debit.totalDebitedToDate || 0, debit.currency)}
              </div>
              <div className="text-xs text-slate-400 font-medium mt-1">
                {debit.numberOfDebits || 0} debits processed
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs Integration - Matching Direct Debit Register Design EXACTLY */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 inline-flex h-10 items-center justify-center rounded-full text-slate-500 mb-6">
          <TabsTrigger 
            value="details" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all flex items-center gap-2"
          >
            <Users className="h-3.5 w-3.5" />
            Direct Debit Details
          </TabsTrigger>
          <TabsTrigger 
            value="schedule" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all flex items-center gap-2"
          >
            <List className="h-3.5 w-3.5" />
            Payment Schedule & Transaction History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-8 mt-0 outline-none focus-visible:ring-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Core Information Section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-slate-50 mb-4">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <List className="h-5 w-5 text-slate-700" />
                  Core Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-y-6 gap-x-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Company</p>
                  <p className="text-base font-semibold text-slate-900">
                    {debit.company && typeof debit.company === 'object' ? (debit.company as any).name : debit.company || '-'}
                  </p>
                  <p className="text-xs text-slate-400 font-medium">
                    {debit.company && typeof debit.company === 'object' ? (debit.company as any).code : ''}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Description</p>
                  <p className="text-base font-semibold text-slate-900 leading-tight">{debit.description}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Reference</p>
                  <p className="text-base font-semibold text-slate-900">{debit.referenceNumber || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Department</p>
                  <p className="text-base font-semibold text-slate-900">{debit.department}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Amount</p>
                  <p className="text-base font-semibold text-slate-900">{formatCurrency(debit.monthlyDebitAmount, debit.currency)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Frequency</p>
                  <div className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="capitalize">{debit.frequency}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">First Debit</p>
                  <p className="text-base font-semibold text-slate-900">{debit.firstDebitDate ? new Date(debit.firstDebitDate).toLocaleDateString('en-AU') : '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Next Scheduled</p>
                  <p className="text-base font-semibold text-slate-900">{debit.status === 'active' && debit.nextScheduledDate ? new Date(debit.nextScheduledDate).toLocaleDateString('en-AU') : '-'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Organizational Details Section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-slate-50 mb-4">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <Users className="h-5 w-5 text-slate-700" />
                  Organizational Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Department</p>
                    <p className="text-base font-semibold text-slate-900">{debit.department}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Cost Centre</p>
                    <p className="text-base font-semibold text-slate-900">{debit.costCentre || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Assigned Contact</p>
                    <p className="text-base font-semibold text-slate-900">{debit.assignedContact || '-'}</p>
                    <p className="text-xs text-slate-400 font-medium">{debit.assignedContactEmail}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Approval Status</p>
                    <div className="mt-1 flex flex-col gap-2">
                      <div>
                        <Badge variant="outline" className={`${getApprovalStatusColor(debit.approvalStatus)} capitalize border-transparent font-semibold px-3 h-6`}>
                          {debit.approvalStatus}
                        </Badge>
                      </div>
                      {debit.approvedBy && (
                        <p className="text-xs text-slate-400 font-medium">
                          Approved by {debit.approvedBy && typeof debit.approvedBy === 'object' ? (debit.approvedBy as any).name : debit.approvedBy} on {debit.approvedDate ? new Date(debit.approvedDate).toLocaleDateString() : 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Details Section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-slate-50 mb-4">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <Banknote className="h-5 w-5 text-slate-700" />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Payee Information</p>
                  <p className="text-base font-semibold text-slate-900">{debit.payeeName}</p>
                  <p className="text-xs text-slate-400 font-medium">ABN: {debit.payeeABN || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Bank/Institution</p>
                    <p className="text-base font-semibold text-slate-900">{debit.bankName || '-'}</p>
                    <p className="text-xs text-slate-400 font-medium ">{debit.institution || ''}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Payment Method</p>
                    <p className="text-base font-semibold text-slate-900">
                      {debit.paymentMethod === 'credit_card' ? 'Credit Card' : 'Bank Account'}
                    </p>
                    <p className="text-xs text-slate-400 font-medium">
                      {debit.paymentMethod === 'credit_card' 
                        ? (debit.creditCardLast4 ? `****${debit.creditCardLast4}` : 'N/A')
                        : (debit.bankAccountLast4 ? `****${debit.bankAccountLast4}` : 'N/A')
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transaction History Summary Section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-slate-50 mb-4">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <History className="h-5 w-5 text-slate-700" />
                  Transaction History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* Row 1: Key Metrics */}
                  <div className="p-4 rounded-xl border border-emerald-100" style={{ backgroundColor: '#F0FDF4' }}>
                    <p className="text-sm font-medium" style={{ color: '#717182' }}>Total Processed</p>
                    <div className="mt-4">
                      <p className="text-2xl font-semibold text-emerald-700">
                        {formatCurrency(totalProcessedAmount, debit.currency)}
                      </p>
                    </div>
                    <p className="text-xs text-emerald-600 font-bold mt-1">{processedTransactions.length} transactions</p>
                  </div>

                  <div className="p-4 rounded-xl border border-blue-100" style={{ backgroundColor: '#EFF6FF' }}>
                    <p className="text-sm font-medium" style={{ color: '#717182' }}>Average Amount</p>
                    <div className="mt-4">
                      <p className="text-2xl font-semibold" style={{ color: '#1447E6' }}>
                        {formatCurrency(avgProcessedAmount, debit.currency)}
                      </p>
                    </div>
                    <p className="text-xs text-blue-600 font-bold mt-1">per transaction</p>
                  </div>

                  {/* Row 2: Initial and Concluding Values */}
                  <div className="p-4 rounded-xl border border-indigo-100" style={{ backgroundColor: '#EFF6FF' }}>
                    <p className="text-sm font-medium" style={{ color: '#717182' }}>First Debit Amount</p>
                    <div className="mt-4">
                      <p className="text-2xl font-semibold" style={{ color: '#1447E6' }}>
                        {formatCurrency(debit.firstDebitAmount || 0, debit.currency)}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200" style={{ backgroundColor: '#EFF6FF' }}>
                    <p className="text-sm font-medium" style={{ color: '#717182' }}>Final Debit Amount (Balloon)</p>
                    <div className="mt-4">
                      <p className="text-2xl font-semibold" style={{ color: '#1447E6' }}>
                        {formatCurrency(debit.finalDebitAmount || 0, debit.currency)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-2">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Last Debit Date</p>
                    <p className="text-base font-semibold text-slate-900">
                      {debit.lastDebitDate ? new Date(debit.lastDebitDate).toLocaleDateString('en-AU') : 'Never'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className={`${getStatusColor(debit.status)} capitalize border-transparent font-semibold h-6 px-3`}>
                        {debit.status === 'suspended' ? 'inactive' : debit.status}
                      </Badge>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-bold ">
                        <Repeat className="h-3.5 w-3.5" />
                        {debit.isRecurring ? 'Recurring' : 'One-time'}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Information and Attachments */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-50">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                <FileText className="h-5 w-5 text-slate-700" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-8">
              {debit.notes && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-500">Notes</Label>
                  <p className="text-sm font-bold text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 leading-relaxed">{debit.notes}</p>
                </div>
              )}
              
              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-500">External Links</Label>
                <div className="flex flex-wrap gap-3">
                  {debit.subscriptionUrl && (
                    <Button variant="outline" size="sm" className="bg-white border-slate-200 text-slate-700 h-9 px-4 font-bold" onClick={() => window.open(debit.subscriptionUrl, '_blank')}>
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                      Subscription Portal
                    </Button>
                  )}
                  {debit.portalLink && (
                    <Button variant="outline" size="sm" className="bg-white border-slate-200 text-slate-700 h-9 px-4 font-bold" onClick={() => window.open(debit.portalLink, '_blank')}>
                      <Link className="h-4 w-4 mr-2" />
                      Billing Portal
                    </Button>
                  )}
                </div>
              </div>

              {debit.attachments && debit.attachments.length > 0 && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <Label className="text-sm font-medium text-slate-500">Attachments</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {debit.attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white shadow-sm hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group">
                        <div className="flex items-center min-w-0 flex-1 cursor-pointer">
                          <div className="p-2 rounded bg-slate-50 mr-3 border border-slate-100 group-hover:bg-white transition-colors">
                            <FileText className="h-4 w-4 text-slate-400 group-hover:text-indigo-500" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-600 truncate mr-2" title={att.fileName}>
                              {att.fileName}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                            onClick={(e) => { e.preventDefault(); window.open(financeApi.getAttachmentDownloadUrl(att.id, 'view'), '_blank'); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                            onClick={(e) => { e.preventDefault(); window.location.href = financeApi.getAttachmentDownloadUrl(att.id, 'download'); }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50"
                            onClick={(e) => { e.preventDefault(); handleDeleteAttachment(att.id, att.fileName); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit Trail Section */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 border-b border-slate-50">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                <ShieldCheck className="h-5 w-5 text-slate-700" />
                Audit Trail & Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              {activityLogs.length > 0 ? (
                <div className="space-y-4">
                  {activityLogs.map((log, idx) => (
                    <div key={log.id} className="flex gap-4 items-start relative pb-4 last:pb-0">
                      {idx !== activityLogs.length - 1 && (
                        <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-slate-100" />
                      )}
                      <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0 z-10">
                        <div className="h-2 w-2 rounded-full bg-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm font-bold text-slate-800 leading-none">
                          {(() => {
                            const desc = log.changeDescription || '';
                            const mainPart = desc.split('|||')[0].replace(/\[ID: .*?\] /, '');
                            
                            // Specific status mapping
                            const lowerDesc = mainPart.toLowerCase();
                            if (lowerDesc.includes('status changed to "suspended"') || mainPart === 'Suspended') {
                              return 'Suspended';
                            }
                            if (lowerDesc.includes('status changed to "active"') || mainPart === 'Activated') {
                              return 'Activated';
                            }
                            if (lowerDesc.startsWith('approval ')) {
                              return mainPart; // e.g. "Approval approved"
                            }
                            if (lowerDesc.startsWith('payment ')) {
                              if (lowerDesc === 'payment suspended') return 'Payment Inactive';
                              return mainPart; // e.g. "Payment processed", "Payment failed", "Payment pending"
                            }

                            if (mainPart.includes(' changed to ') || mainPart.includes(' assigned to ') || mainPart.includes(' unassigned')) {
                              return 'Updated';
                            }
                            return mainPart.replace(/ record$/, '') || 'Updated';
                          })()}
                        </p>
                        <p className="text-[11px] font-medium text-slate-500 leading-none">
                          {log.timestamp ? (() => {
                            const d = new Date(log.timestamp);
                            const pad = (n: number) => n.toString().padStart(2, '0');
                            return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                          })() : '-'}
                        </p>
                        <p className="text-xs text-slate-400 leading-none">
                          by <span className="font-bold text-slate-600">{(log.user as any)?.name || (log.user as any)?.username || 'System'}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
                  <div className="h-10 w-10 rounded-full bg-slate-50 flex items-center justify-center">
                    <History className="h-5 w-5 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-400">No activity history recorded yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-8 mt-0 outline-none focus-visible:ring-0">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-slate-500">Total Scheduled</p>
                <div className="mt-4">
                  <span className="text-2xl font-semibold text-slate-900">{formatCurrency(totalScheduledAmount, debit.currency)}</span>
                </div>
                <p className="text-sm text-slate-400 font-medium mt-1">{scheduledTransactions.length} payments</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-emerald-600 ">Total Processed</p>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-emerald-600">{formatCurrency(totalProcessedAmount, debit.currency)}</span>
                </div>
                <p className="text-sm text-emerald-500/70 font-medium mt-1">{processedTransactions.length} payments</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-rose-600 ">Failed Payments</p>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-rose-600">{failedTransactions.length}</span>
                </div>
                <p className="text-sm text-rose-500/70 font-medium mt-1">Require attention</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-blue-600 ">Adjustments Made</p>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-blue-600">{adjustmentTransactions.length}</span>
                </div>
                <p className="text-sm text-blue-500/70 font-medium mt-1">Custom amounts</p>
              </CardContent>
            </Card>
          </div>

          {/* Direct Debit Details Bar */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-base font-medium text-slate-900 mb-6">Direct Debit Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Payee</p>
                  <p className="text-sm font-semibold text-slate-900">{debit.payeeName}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Frequency</p>
                  <p className="text-sm font-bold text-slate-900 capitalize">{debit.frequency}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Regular Amount</p>
                  <p className="text-sm font-semibold text-slate-900">{formatCurrency(debit.monthlyDebitAmount, debit.currency)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">First Payment</p>
                  <p className="text-sm font-semibold text-slate-900">{debit.firstDebitDate ? new Date(debit.firstDebitDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payments Table */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 border-b border-slate-50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base font-bold text-slate-900">All Payments ({debit.transactions?.length || 0})</CardTitle>
                  <CardDescription className="text-xs font-medium text-slate-500">View and edit individual payment amounts and schedules</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-normal py-3 text-slate-900 w-12">#</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Scheduled Date</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Amount</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Status</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Processed Date</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Actual Amount</TableHead>
                    <TableHead className="font-normal py-3 text-slate-900">Notes</TableHead>
                    <TableHead className="text-right font-normal py-3 text-slate-900">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debit.transactions && debit.transactions.length > 0 ? (
                    debit.transactions.map((tx, idx) => (
                      <TableRow key={tx.id} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-0">
                        <TableCell className="text-xs font-normal text-slate-400">{idx + 1}</TableCell>
                        <TableCell className="text-sm font-normal text-slate-900">
                          {new Date(tx.dueDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-sm font-normal text-slate-900">{formatCurrency(tx.amount, debit.currency)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`h-6 text-[9px] font-bold px-2 border-transparent ${
                            tx.status === 'processed' ? 'bg-slate-900 text-white' : 
                            tx.status === 'pending' ? 'bg-indigo-100 text-indigo-700' : 
                            getStatusColor(tx.status)
                          }`}>
                            <div className="flex items-center gap-1.5 uppercase tracking-wider">
                              {tx.status === 'processed' ? <CheckCircle className="h-3 w-3" /> : <Hourglass className="h-3 w-3" />}
                              {tx.status === 'suspended' ? 'Inactive' : tx.status}
                            </div>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-medium text-slate-600">
                          {tx.processedDate ? new Date(tx.processedDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-normal text-slate-900">
                          {tx.actualAmount ? formatCurrency(tx.actualAmount, debit.currency) : '-'}
                        </TableCell>
                        <TableCell className="text-xs font-medium text-slate-600 max-w-[200px] truncate">{tx.notes || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={() => handleEditTx(tx)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-400 font-medium italic">No payment history found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transaction Edit Dialog */}
      <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
        <DialogContent className="max-w-2xl border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Edit Payment Schedule Entry</DialogTitle>
            <DialogDescription className="font-medium text-slate-500">Update the date, amount, or processing status for this specific entry.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Scheduled Date</Label>
                <Input type="date" className="bg-slate-100 border-transparent focus-visible:ring-slate-900 h-10" value={txForm.dueDate} onChange={(e) => setTxTxForm({...txForm, dueDate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Amount ({debit.currency})</Label>
                <Input type="number" step="0.01" className="bg-slate-100 border-transparent focus-visible:ring-slate-900 h-10" value={txForm.amount} onChange={(e) => setTxTxForm({...txForm, amount: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Status</Label>
              <Select 
                value={txForm.status} 
                onValueChange={(v: any) => {
                  const newDate = v === 'processed' ? new Date().toISOString().split('T')[0] : '';
                  setTxTxForm({...txForm, status: v, processedDate: newDate, actualAmount: txForm.actualAmount || txForm.amount});
                }}
              >
                <SelectTrigger className="bg-slate-100 border-transparent focus:ring-slate-900 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="suspended">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {txForm.status === 'processed' && (
              <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Actual Processed Date</Label>
                  <Input type="date" className="bg-emerald-50 border-emerald-100 focus-visible:ring-emerald-500 h-10" value={txForm.processedDate || ''} onChange={(e) => setTxTxForm({...txForm, processedDate: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Actual Amount Paid ({debit.currency})</Label>
                  <Input type="number" step="0.01" className="bg-emerald-50 border-emerald-100 focus-visible:ring-emerald-500 h-10" value={txForm.actualAmount || 0} onChange={(e) => setTxTxForm({...txForm, actualAmount: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Adjustment Reason</Label>
              <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 h-10" placeholder="e.g., Annual price increase" value={txForm.adjustmentReason} onChange={(e) => setTxTxForm({...txForm, adjustmentReason: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Notes</Label>
              <Textarea placeholder="Additional context..." className="bg-slate-100 border-transparent focus-visible:ring-slate-900" rows={3} value={txForm.notes} onChange={(e) => setTxTxForm({...txForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter className="border-t border-slate-50 pt-4">
            <Button variant="outline" className="bg-white border-slate-200 text-slate-700" onClick={() => setShowTxDialog(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm font-bold" onClick={handleSaveTx}><Save className="h-4 w-4 mr-2" /> Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteAttachmentDialog 
        open={showDeleteAttDialog}
        onOpenChange={setShowDeleteAttDialog}
        onConfirm={confirmDeleteAttachment}
        fileName={attachmentToDelete?.name}
      />
    </div>
  );
};

export default DirectDebitDetailsPage;
